import monotoneChainConvexHull from "monotone-chain-convex-hull";
import { Polygon } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import { Cluster } from "ol/source";
import VectorSource from "ol/source/Vector";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style";

export const clusterMap = ({ map, featureCollection, activeLayers, setActiveLayers }) => {
    const distanceSwiper = document.getElementById('distanceSwiper');
    const minDistanceSwiper = document.getElementById('min-distanceSwiper');
    const pointViewZoom = 13;

    let clusterLayer;
    let clusterSource;
    let clusterHulls;
    let pointLayer;

    let hoverFeature;
    const clusterStyleCache = {};

    const getClusterRadius = (size) => {
        const minRadius = 9;
        const maxRadius = 26;
        const scaledRadius = minRadius + Math.log2(size) * 2.2;

        return Math.max(minRadius, Math.min(maxRadius, scaledRadius));
    };

    const getClusterPalette = (size) => {
        let baseColor = "33, 150, 243";

        if (size >= 100) {
            baseColor = "244, 67, 54";
        } else if (size >= 30) {
            baseColor = "255, 152, 0";
        }

        return {
            outerColor: `rgba(${baseColor}, 0.22)`,
            outerStroke: `rgba(${baseColor}, 0.34)`,
            innerColor: `rgba(${baseColor}, 0.56)`,
        };
    };

    const clusterStyleFn = (feature) => {
        const size = feature.get("features").length;
        
        if (!clusterStyleCache[size]) {
            const radius = getClusterRadius(size);
            const fontSize = Math.max(11, Math.min(16, Math.round(radius * 0.78)));
            const palette = getClusterPalette(size);

            clusterStyleCache[size] = [
                new Style({
                    image: new CircleStyle({
                        radius: radius + 5,
                        fill: new Fill({
                            color: palette.outerColor
                        }),
                        stroke: new Stroke({
                            color: palette.outerStroke,
                            width: 1.2
                        })
                    })
                }),
                new Style({
                    image: new CircleStyle({
                        radius: radius,
                        stroke: new Stroke({
                            color: "rgba(255, 255, 255, 0.85)",
                            width: 1.6
                        }),
                        fill: new Fill({
                            color: palette.innerColor
                        })
                    }),
                    text: new Text({
                        text: size.toString(),
                        font: `600 ${fontSize}px "Pretendard", "Noto Sans KR", sans-serif`,
                        fill: new Fill({
                            color: "#fff"
                        }),
                        stroke: new Stroke({
                            color: "rgba(0, 0, 0, 0.35)",
                            width: 2.2
                        })
                    })
                })
            ];
        }

        return clusterStyleCache[size];
    }

    const pointStyle = new Style({
        image: new CircleStyle({
            radius: 3.3,
            stroke: new Stroke({
                color: "rgba(255, 255, 255, 0.9)",
                width: 1
            }),
            fill: new Fill({
                color: "rgba(0, 103, 163, 0.8)"
            })
        })
    });

    const updateZoomLayerVisibility = () => {
        const zoom = map.getView().getZoom();
        const showPoints = zoom >= pointViewZoom;

        if (clusterLayer) {
            clusterLayer.setVisible(!showPoints);
        }
        if (clusterHulls) {
            clusterHulls.setVisible(!showPoints);
        }
        if (pointLayer) {
            pointLayer.setVisible(showPoints);
        }

        if (showPoints) {
            hoverFeature = null;
            if (clusterHulls) {
                clusterHulls.changed();
            }
        }
    };

    const hullStyleFn = (cluster) => {
        if(cluster !== hoverFeature) {
            return null;
        }

        const originalFeatures = cluster.get('features');
        const points = originalFeatures.map((feature) =>
          feature.getGeometry().getCoordinates()
        );
        return new Style({
          geometry: new Polygon([monotoneChainConvexHull(points)]),
          fill: new Fill({
            color: 'rgba(255, 153, 0, 0.4)',
          }),
          stroke: new Stroke({
            color: 'rgba(204, 85, 0, 1)',
            width: 1.5,
          }),
        });
    }

    const distanceRenderHandle = () => {
        const distance = distanceSwiper.value;

        clusterSource.setDistance(parseInt(distance, 10));

        map.render();
    }

    const minDistanceRenderHandle = () => {
        const minDistance = minDistanceSwiper.value;

        clusterSource.setMinDistance(parseInt(minDistance, 10));

        map.render();
    }

    const clusterHoverHandle = (e) => {
        if (!clusterLayer || !clusterLayer.getVisible()) {
            return;
        }

        clusterLayer.getFeatures(e.pixel).then((features) => {
            if (features[0] !== hoverFeature) {
            hoverFeature = features[0];
            clusterHulls.setStyle(hullStyleFn);
            }
        });
    };

    const mount = () => {
        if(activeLayers) {
            setActiveLayers();
        }

        const features = [];
    
        featureCollection.forEach(collection => {
            features.push(...collection.featureArray);
        });

        const source = new VectorSource({
            features: features
        });

        clusterSource = new Cluster({
            distance: parseInt(distanceSwiper.value, 10),
            minDistance: parseInt(minDistanceSwiper.value, 10),
            source: source
        })

        
        clusterLayer = new VectorLayer({
            source: clusterSource,
            style: clusterStyleFn,
            zIndex: 100
        })
        
        clusterHulls = new VectorLayer({
            source: clusterSource,
            style: hullStyleFn
        })

        pointLayer = new VectorLayer({
            source: source,
            style: pointStyle,
            visible: false,
            zIndex: 101
        })

        setActiveLayers([clusterLayer, clusterHulls, pointLayer])

        distanceSwiper.addEventListener('input', distanceRenderHandle);
        minDistanceSwiper.addEventListener('input', minDistanceRenderHandle);

        map.on('pointermove', clusterHoverHandle);
        map.on('moveend', updateZoomLayerVisibility);

        updateZoomLayerVisibility();

        map.render();
    }

    const dismount = () => {
        if(distanceSwiper && minDistanceSwiper){
            distanceSwiper.removeEventListener('input', distanceRenderHandle);
            minDistanceSwiper.removeEventListener('input', minDistanceRenderHandle);
        }
        map.un('pointermove', clusterHoverHandle);
        map.un('moveend', updateZoomLayerVisibility);
        setActiveLayers();
        map.render();
    }

    return { mount: mount, dismount: dismount };
}
